import { formatDateTimeFull } from '../../../lib/datetime';
import { formatDuration } from '../../../lib/format';
import { useJobLogs } from '../hooks/useJobLogs';
import { jobsApi } from '../services/jobsApi';
import type { JobRunDetail } from '../types/jobs';
import { JobLogViewer } from './JobLogViewer';
import { JobStatusBadge } from './JobStatusBadge';

const ACTIVE_STATUSES = new Set(['pending', 'running']);

interface JobRunDetailsProps {
  projectUuid: string;
  detail: JobRunDetail;
}

export function JobRunDetails({ projectUuid, detail }: JobRunDetailsProps) {
  const isActive = ACTIVE_STATUSES.has(detail.status);
  const {
    logs,
    hasMore,
    loading: logsLoading,
    loadMore,
  } = useJobLogs(projectUuid, detail.uuid, isActive);

  const gcpUrl = jobsApi.gcpLogUrl(detail.externalLogRef);

  return (
    <div className="job-detail">
      <dl className="job-detail__meta">
        <dt>Started</dt>
        <dd>{formatDateTimeFull(detail.startedAt)}</dd>

        <dt>Completed</dt>
        <dd>{formatDateTimeFull(detail.completedAt)}</dd>

        <dt>Duration</dt>
        <dd>{formatDuration(detail.durationMs)}</dd>

        <dt>Triggered by</dt>
        <dd>
          {detail.triggerType === 'manual' ? (detail.triggeredByEmail ?? 'Manual') : 'Scheduled'}
        </dd>
      </dl>

      {detail.syncResults && (
        <div className="job-detail__sync-results">
          <h3 className="job-detail__section-title">Sync Results</h3>
          <dl className="job-detail__meta">
            <dt>Status</dt>
            <dd>
              <JobStatusBadge status={detail.status} />
            </dd>
            <dt>Fetched</dt>
            <dd>{detail.syncResults.recordsFetched}</dd>
            <dt>Created</dt>
            <dd>{detail.syncResults.recordsCreated}</dd>
            <dt>Updated</dt>
            <dd>{detail.syncResults.recordsUpdated}</dd>
            <dt>Unchanged</dt>
            <dd>{detail.syncResults.recordsUnchanged}</dd>
            {detail.syncResults.recordsSkipped > 0 && (
              <>
                <dt>Skipped</dt>
                <dd>
                  {detail.syncResults.recordsSkipped}
                  {detail.syncResults.skipReason && (
                    <span className="job-detail__meta-note">
                      {' '}
                      ({detail.syncResults.skipReason})
                    </span>
                  )}
                </dd>
              </>
            )}
            <dt>Failed</dt>
            <dd className={detail.syncResults.recordsFailed > 0 ? 'job-detail__warn' : ''}>
              {detail.syncResults.recordsFailed}
            </dd>
          </dl>
          {detail.status === 'skipped' && detail.errorSummary && (
            <p className="job-detail__skip-reason">{detail.errorSummary}</p>
          )}
        </div>
      )}

      {detail.errorSummary && detail.status !== 'skipped' && (
        <div className="job-detail__error">
          <h3 className="job-detail__section-title">Error</h3>
          <p>{detail.errorSummary}</p>
        </div>
      )}

      {gcpUrl && (
        <div className="job-detail__gcp-link">
          <a href={gcpUrl} target="_blank" rel="noopener noreferrer">
            View in GCP Cloud Logging ↗
          </a>
        </div>
      )}

      <div className="job-detail__logs">
        <h3 className="job-detail__section-title">
          Logs
          {isActive && <span className="job-detail__live-badge">Live</span>}
        </h3>
        <JobLogViewer logs={logs} hasMore={hasMore} loading={logsLoading} onLoadMore={loadMore} />
      </div>
    </div>
  );
}
